import React, { useCallback, useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  __,
  chain,
  concat,
  find,
  isEmpty,
  lensProp,
  map,
  pipe,
  prop,
  propEq,
  set,
  uniqBy,
} from "ramda";
import { HIDE_VIEWER } from "./mediaViewerRedux";
import { useDispatch, useSelector } from "react-redux";
import debounce from "lodash.debounce";
import { useContentPages } from "../creator-application/content/contentServices";
import { useImageBookViewerSearch } from "../creator-application/searchServices";
import { getElementDimensions } from "biblioboard-pdf-viewer/es/util/domUtil";
import {
  BASE_TRANSFORM,
  calculateScaleThatFits,
  scaleWithinContainer,
} from "biblioboard-pdf-viewer/es/util/translationUtil";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight } from "@fortawesome/pro-regular-svg-icons";

import AppBar from "@material-ui/core/AppBar";
import Box from "@material-ui/core/Box";
import Close from "@material-ui/icons/Close";
import Dialog from "@material-ui/core/Dialog";
import IconButton from "@material-ui/core/IconButton";
import Popover from "@material-ui/core/Popover";
import SearchIcon from "@material-ui/icons/Search";
import Slider from "@material-ui/core/Slider";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import ZoomOutIcon from "@material-ui/icons/ZoomOut";
import ZoomOutMap from "@material-ui/icons/ZoomOutMap";

import ImageBookViewer from "./ImageBookViewer";
import SearchDialog from "media-viewer/SearchDialog";

const useStyles = makeStyles(theme => ({
  title: {
    textAlign: "center",
    flex: 1,
    color: theme.palette.text.secondary,
  },

  appBar: {
    backgroundColor: "white",
    boxShadow: "none",
  },

  appBarBottom: {
    top: "auto",
    bottom: 0,
    backgroundColor: "white",
    boxShadow: "none",
  },

  controls: {
    height: 200,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-around",
    alignItems: "center",
    width: "inherit",
    background: "white",
    borderRadius: 50,
    boxShadow: "0 5px 10px 0 rgba(50,50,50,.2)",
  },

  controlsButton: {
    background: theme.palette.primary.lightBlue.link,
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.primary.lightBlue.link,
    },
  },

  controlsContainer: {
    position: "fixed",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    height: "inherit",
    minHeight: "inherit",
    width: 50,
    top: 0,
    bottom: 0,
    right: 30,
  },

  zoomMenuPaper: {
    height: 300,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },

  zoomSlider: {
    flex: 1,
  },

  zoomSliderIcon: {
    margin: "0px 40px 0px 20px",
    height: 60,
  },

  pager: {
    fontWeight: "bold",
  },

  slider: {
    margin: "0 20px",
  },

  sliderTrack: {
    color: theme.palette.text.secondary,
    ".Mui-disabled &": {
      color: theme.palette.grey[400],
    },
  },

  sliderRail: {
    color: theme.palette.grey[800],
    ".Mui-disabled &": {
      color: theme.palette.grey[400],
    },
  },

  sliderThumb: {
    backgroundColor: theme.palette.grey["A400"],
    "&:hover": {
      boxShadow: "0px 0px 0px 8px rgba(0, 0, 0, .1)",
    },
    "&:active": {
      boxShadow: "0px 0px 0px 14px rgba(0, 0, 0, .1)",
    },
    ".Mui-disabled &": {
      backgroundColor: theme.palette.grey[400],
    },
  },

  sliderMarkLabel: {
    fontSize: "1.4rem",
    fontWeight: 700,
  },

  sliderValueLabel: {
    fontSize: "1.4rem",
    fontWeight: 700,
    color: "black",
  },
}));

const zoomMarks = [
  {
    value: 4,
    label: "4x",
  },
  {
    value: 2,
    label: "2x",
  },
  {
    value: 0,
    label: "1x",
  },
  {
    value: -2,
    label: ".5x",
  },
  {
    value: -4,
    label: ".25x",
  },
];

const LIMIT = 20;

const ImageBookViewerDialog = () => {
  const classes = useStyles();

  const { media } = useSelector(state => state.mediaViewer);
  const dispatch = useDispatch();

  const onClose = () => dispatch({ type: HIDE_VIEWER });

  const viewportRef = useRef();
  const imageRef = useRef();
  const [zoomMenuAnchor, setZoomMenuAnchor] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [transform, setTransform] = useState(BASE_TRANSFORM);

  const getViewportDimensions = () =>
    viewportRef.current.getBoundingClientRect();
  const getImageDimensions = () => getElementDimensions(imageRef.current);

  //-- Pages
  const [pagesSearchState, setPagesSearchState] = useState({
    limit: LIMIT,
    offset: 0,
  });
  const contentPages = useContentPages(media.id, pagesSearchState);

  const [pages, setPages] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [requestedPageNumber, setRequestedPageNumber] = useState(1);
  const [currentPage, setCurrentPage] = useState(null);
  useEffect(() => {
    if (contentPages.isSuccess) {
      setPages(
        pipe(concat(contentPages.data), uniqBy(prop("logicalPageIndex"))),
      );
    }
  }, [contentPages.isSuccess, contentPages.data]);

  useEffect(() => {
    if (!isEmpty(pages)) {
      const page = find(propEq("logicalPageIndex", pageNumber - 1), pages);
      if (page) {
        setCurrentPage(page);
      } else {
        const offset = Math.floor(pageNumber / LIMIT) * LIMIT;
        setPagesSearchState(set(lensProp("offset"), offset));
      }
    }
  }, [pages, pageNumber]);

  const debouncedUpdatePageNumber = useCallback(
    debounce(pageNumber => {
      setPageNumber(pageNumber);
    }, 0),
    [],
  );
  //-- End Pages

  //-- Zoom
  const zoomToScale = useCallback(
    scale => {
      const maxScale = 4;
      const minScale = -4;

      const newScale =
        scale > maxScale ? maxScale : scale < minScale ? minScale : scale;

      const imageDimensions = getImageDimensions();
      const newTransform = scaleWithinContainer(
        getViewportDimensions(),
        imageDimensions,
        newScale,
        transform,
      );
      setTransform(newTransform);
    },
    [transform],
  );
  //-- End Zoom

  //-- Search
  const [searchQuery, setSearchQuery] = useState("");
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [searchResults, setSearchResults] = useState([]);

  const search = useImageBookViewerSearch(media.id, searchQuery);
  useEffect(() => {
    if (search.isSuccess && !isEmpty(search.data)) {
      const results = chain(result => {
        const { logicalPageIndex, context } = result;
        return map(
          match => ({
            pageIndex: logicalPageIndex,
            excerpt: match,
          }),
          context,
        );
      }, search.data);
      setSearchResults(results);
    }
  }, [search.isSuccess, search.data]);

  const handleSearch = query => {
    setSearchQuery(query);
  };

  const handleClearSearch = () => {
    setSearchResults([]);
    setSearchQuery("");
  };

  const handleSearchDialogClose = () => {
    setSearchDialogOpen(false);
  };

  const handleSearchItemClick = item => {
    setPageNumber(parseInt(item.pageIndex, 10) + 1);
    setRequestedPageNumber(parseInt(item.pageIndex, 10) + 1);
    setSearchDialogOpen(false);
  };
  //-- End Search

  //-- Paging
  const setPreviousPage = prev => (prev > 1 ? prev - 1 : prev);
  const handlePreviousPage = () => {
    setRequestedPageNumber(setPreviousPage);
    debouncedUpdatePageNumber(setPreviousPage);
  };

  const setNextPage = useCallback(
    prev => (prev === media.numberOfPages ? prev : prev + 1),
    [media.numberOfPages],
  );
  const handleNextPage = () => {
    setRequestedPageNumber(setNextPage);
    debouncedUpdatePageNumber(setNextPage);
  };
  //-- End Paging

  return (
    <Dialog fullScreen onClose={onClose} aria-label="book viewer" open>
      <AppBar className={classes.appBar} position="relative">
        <Toolbar variant="dense">
          <Typography variant="h5" className={classes.title}>
            {media.title}
          </Typography>

          <IconButton edge="end" onClick={onClose} aria-label="close">
            <Close />
          </IconButton>
        </Toolbar>
      </AppBar>

      <ImageBookViewer
        ref={{ viewport: viewportRef, image: imageRef }}
        pagesLoading={contentPages.isLoading}
        pages={pages}
        currentPage={currentPage}
        transform={transform}
        setTransform={setTransform}
        handlePreviousPage={handlePreviousPage}
        handleNextPage={handleNextPage}
      />

      <div className={classes.controlsContainer}>
        <div className={classes.controls}>
          <IconButton
            aria-label="go to previous page"
            className={classes.controlsButton}
            onClick={handlePreviousPage}
          >
            <FontAwesomeIcon icon={faArrowLeft} size="lg" />
          </IconButton>

          <Box textAlign="center">
            <Typography className={classes.pager}>{pageNumber}</Typography>
            <Typography>of</Typography>
            <Typography className={classes.pager}>
              {media.numberOfPages}
            </Typography>
          </Box>

          <IconButton
            aria-label="go to next page"
            className={classes.controlsButton}
            onClick={handleNextPage}
          >
            <FontAwesomeIcon icon={faArrowRight} size="lg" />
          </IconButton>
        </div>
      </div>

      <AppBar position="relative" className={classes.appBarBottom}>
        <Toolbar variant="dense">
          <IconButton
            aria-label="search"
            onClick={() => setSearchDialogOpen(true)}
          >
            <SearchIcon />
          </IconButton>
          <IconButton
            aria-label="zoom"
            onClick={event => setZoomMenuAnchor(event.currentTarget)}
          >
            <ZoomInIcon />
          </IconButton>
          <Popover
            anchorEl={zoomMenuAnchor}
            open={Boolean(zoomMenuAnchor)}
            onClose={() => setZoomMenuAnchor(null)}
            classes={{
              paper: classes.zoomMenuPaper,
            }}
            PaperProps={{
              onMouseLeave: () => setZoomMenuAnchor(null),
            }}
          >
            <ZoomInIcon className={classes.zoomSliderIcon} />
            <Slider
              track={false}
              className={classes.zoomSlider}
              orientation="vertical"
              marks={zoomMarks}
              max={4}
              min={-4}
              value={Math.log2(zoom) * 2}
              aria-labelledby="zoom slider"
              onChange={(e, value) => {
                setZoom(Math.pow(2, value / 2));
                zoomToScale(Math.pow(2, value / 2));
              }}
              classes={{
                rail: classes.sliderRail,
                track: classes.sliderTrack,
                thumb: classes.sliderThumb,
                markLabel: classes.sliderMarkLabel,
              }}
            />
            <ZoomOutIcon className={classes.zoomSliderIcon} />
          </Popover>

          <IconButton
            aria-label="zoom to fit"
            onClick={() => {
              const zoomToFit = calculateScaleThatFits(
                getViewportDimensions(),
                getImageDimensions(),
                BASE_TRANSFORM,
              );
              zoomToScale(zoomToFit);
            }}
          >
            <ZoomOutMap />
          </IconButton>

          <Slider
            min={1}
            max={media.numberOfPages}
            valueLabelDisplay="auto"
            className={classes.slider}
            value={requestedPageNumber}
            onChange={(e, value) => {
              setRequestedPageNumber(value);
              debouncedUpdatePageNumber(value);
            }}
            classes={{
              rail: classes.sliderRail,
              track: classes.sliderTrack,
              thumb: classes.sliderThumb,
              valueLabel: classes.sliderValueLabel,
            }}
          />
        </Toolbar>
      </AppBar>
      <SearchDialog
        open={searchDialogOpen}
        loading={search.isLoading}
        searchResults={searchResults}
        onSearch={handleSearch}
        onClear={handleClearSearch}
        onClose={handleSearchDialogClose}
        onItemClick={handleSearchItemClick}
      />
    </Dialog>
  );
};

export default ImageBookViewerDialog;
